import React from 'react';
import { Link } from 'react-router-dom';
import './Support.css';

const Support = () => {
  const faqs = [
    {
      question: 'How do I get started with RunningAI Pro?',
      answer: 'Download RunningAI Pro from the App Store, create a free account using your email, and start tracking your runs immediately. The app will guide you through the setup process.'
    },
    {
      question: 'What features are available in the free version?',
      answer: 'Free users can track unlimited runs with GPS, access the community, and get 3 AI coach messages, 2 run analyses, and 1 training plan per month.'
    },
    {
      question: 'How do I upgrade to Premium?',
      answer: 'Open the app, tap on your profile, then tap "Upgrade to Premium". Choose your preferred subscription plan (monthly or yearly) and complete the purchase through Apple\'s in-app purchase system.'
    },
    {
      question: 'How do I cancel my Premium subscription?',
      answer: 'Go to Settings on your iPhone > tap your name > Subscriptions > select RunningAI Pro > tap Cancel Subscription. Your Premium access will continue until the end of your current billing period.'
    },
    {
      question: 'How accurate is the GPS tracking?',
      answer: 'RunningAI Pro uses your device\'s GPS for highly accurate tracking. For best results, ensure location services are enabled and you have a clear view of the sky when starting your run.'
    },
    {
      question: 'Can I sync my data across devices?',
      answer: 'Yes! Your data is automatically synced to the cloud when you\'re signed in. Log in with the same account on any device to access your running history.'
    },
    {
      question: 'How does the AI coach work?',
      answer: 'Our AI coach uses advanced language models trained on running expertise to provide personalized advice, training plans, and answer your running-related questions based on your activity history.'
    },
    {
      question: 'How do I delete my account?',
      answer: 'Go to Settings in the app > Account Settings > Delete Account. Note that this action is permanent and will delete all your data.'
    },
    {
      question: 'How do I request a refund?',
      answer: 'Refunds are processed through Apple. Visit reportaproblem.apple.com, sign in with your Apple ID, find your RunningAI Pro purchase, and request a refund. Apple reviews all refund requests.'
    },
    {
      question: 'Is my data private and secure?',
      answer: 'Yes! We take privacy seriously. Your data is encrypted and stored securely. We never sell your personal information. Read our Privacy Policy for complete details.'
    }
  ];

  const troubleshooting = [
    {
      issue: 'GPS not working',
      solution: 'Ensure Location Services are enabled for RunningAI Pro in Settings > Privacy & Security > Location Services. Make sure you have "Allow While Using App" or "Always" selected.'
    },
    {
      issue: 'App crashes or freezes',
      solution: 'Try force-closing the app and reopening it. If the problem persists, restart your device. Make sure you\'re running the latest version of RunningAI Pro from the App Store.'
    },
    {
      issue: 'Run not recording',
      solution: 'Check that you have location permissions enabled. Ensure you have internet connectivity for the initial GPS lock. Wait a few seconds after tapping "Start Run" for GPS to acquire your position.'
    },
    {
      issue: 'Unable to sign in',
      solution: 'Double-check your email and password. If you forgot your password, use the "Forgot Password" link on the login screen. Ensure you have a stable internet connection.'
    },
    {
      issue: 'Premium features not unlocking',
      solution: 'Sign out and sign back in to refresh your subscription status. If the issue persists, go to Settings > Restore Purchases. Allow up to 24 hours for subscription to activate.'
    }
  ];

  return (
    <div className="support-page">
      {/* Header */}
      <header className="support-header">
        <div className="container">
          <Link to="/" className="back-button">← Back to Home</Link>
          <h1>Support Center</h1>
          <p className="subtitle">Get help with RunningAI</p>
        </div>
      </header>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <h2>Contact Us</h2>
          <p>Need help with RunningAI Pro? We're here for you!</p>
          <div className="contact-card">
            <div className="contact-icon">✉️</div>
            <h3>Email Support</h3>
            <a href="mailto:support@runningaipro.com" className="email-link">support@runningaipro.com</a>
            <p className="response-time">We typically respond within 24-48 hours</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h3 className="faq-question">{faq.question}</h3>
                <p className="faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Troubleshooting Section */}
      <section className="troubleshooting-section">
        <div className="container">
          <h2>Troubleshooting</h2>
          <div className="troubleshooting-list">
            {troubleshooting.map((item, index) => (
              <div key={index} className="troubleshooting-item">
                <h3 className="issue-title">🔧 {item.issue}</h3>
                <p className="solution">{item.solution}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="resources-section">
        <div className="container">
          <h2>Additional Resources</h2>
          <div className="resource-links">
            <Link to="/privacy" className="resource-link">
              <span className="resource-icon">🔒</span>
              <span>Privacy Policy</span>
            </Link>
            <Link to="/terms" className="resource-link">
              <span className="resource-icon">📄</span>
              <span>Terms of Service</span>
            </Link>
            <a href="https://apps.apple.com/app/runningaipro" target="_blank" rel="noopener noreferrer" className="resource-link">
              <span className="resource-icon">📱</span>
              <span>Download on App Store</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="support-footer">
        <div className="container">
          <p>&copy; 2026 RunningAI Pro. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Support;
